import React, { useState, useEffect } from 'react';
import { X, Send, User, Mail, Phone, Briefcase, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuickContactModal = ({ isOpen, onClose, defaultService = '' }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: defaultService,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update service when defaultService changes
  useEffect(() => {
    if (defaultService) {
      setFormData(prev => ({ ...prev, service: defaultService }));
    }
  }, [defaultService]);

  const services = [
    'Dubai Mainland Company',
    'Free Zone Setup',
    'Offshore Company',
    'Corporate Tax & VAT',
    'Visa Services',
    'Banking Assistance',
    'Business Consultancy',
    'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      toast({
        title: "Request Submitted Successfully!",
        description: "Our team will contact you within 24 hours.",
      });
      
      // Reset form and close modal
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Get Started Today</h2>
              <p className="text-white/90 text-sm">Fill in your details and we'll contact you within 24 hours</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-all"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="quick-name" className="text-gray-700 font-medium flex items-center gap-2">
              <User size={16} className="text-[#6B1B4D]" />
              Full Name *
            </Label>
            <Input
              id="quick-name"
              type="text"
              placeholder="John Doe"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="mt-1 h-12"
            />
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="quick-email" className="text-gray-700 font-medium flex items-center gap-2">
              <Mail size={16} className="text-[#6B1B4D]" />
              Email Address *
            </Label>
            <Input
              id="quick-email"
              type="email"
              placeholder="john@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="mt-1 h-12"
            />
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="quick-phone" className="text-gray-700 font-medium flex items-center gap-2">
              <Phone size={16} className="text-[#6B1B4D]" />
              Phone Number *
            </Label>
            <Input
              id="quick-phone"
              type="tel"
              placeholder="+971 50 123 4567"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="mt-1 h-12"
            />
          </div>

          {/* Service */}
          <div>
            <Label htmlFor="quick-service" className="text-gray-700 font-medium flex items-center gap-2">
              <Briefcase size={16} className="text-[#6B1B4D]" />
              Service Interested In *
            </Label>
            <Select 
              value={formData.service} 
              onValueChange={(value) => setFormData({ ...formData, service: value })}
              required
            >
              <SelectTrigger className="mt-1 h-12">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {services.map((service, idx) => (
                  <SelectItem key={idx} value={service}>
                    {service}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="quick-message" className="text-gray-700 font-medium flex items-center gap-2">
              <MessageSquare size={16} className="text-[#6B1B4D]" />
              Brief Message (Optional)
            </Label>
            <Textarea
              id="quick-message"
              placeholder="Tell us about your business requirements..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="mt-1 min-h-24"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white hover:shadow-lg transition-all h-12 text-base font-semibold"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="mr-2" size={18} />
                Submit Request
              </>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            By submitting, you agree to our Privacy Policy and Terms of Service
          </p>
        </form>
      </div>
    </div>
  );
};

export default QuickContactModal;
